// components/PopupAttorney.js
import React, {useEffect, useState, useContext, useCallback} from 'react';
import api from '../api';
import './shore-more.css';
import {SearchContext} from "../context/SearchContext";
import EditAwardsPopup from "./popups/EditAwardsPopup";
import EditLanguagesPopup from "./popups/EditLanguagesPopup";
import {useUser} from "../context/UserContext";
import Cookies from "js-cookie";
import EditProfilePopup from "./popups/EditProfilePopup";
import EditEducationsPopup from "./popups/EditEducationsPopup";
import EditPreviousWorkplacesPopup from "./popups/EditPreviousWorkplacesPopup";
import EditSpecializationsPopup from "./popups/EditSpecializationsPopup";
import {useNotification} from "../context/NotificationContext";
import Notification from "./Notification";
import SendMessageModal from "./popups/SendMessageModal";
import RequestCallbackModal from "./popups/RequestCallbackModal"; // Import needed modal
import CropImageModal from './popups/CropImageModal';
import {validateAndScaleImage} from '../utils/validateAndScaleImage';

const IMAGE_REQUIREMENTS = {
    'attorney_photo': {'min_width': 172, 'min_height': 172, 'aspect_ratio': 1.0, 'ratio_tolerance': 0.05,},
    'background_attorney': {'min_width': 1440, 'min_height': 280, 'aspect_ratio': 5.14, 'ratio_tolerance': 0.05,},
};

const PopupAttorney = ({visible, attorney, onClose, showSharePopup, origin}) => {
    // --- State Variables ---
    const [legalCompanies, setLegalCompanies] = useState([]); // State to hold associated companies
    const [showAllCompanies, setShowAllCompanies] = useState(false);
    const [showAllPreviousWorkplaces, setShowAllPreviousWorkplaces] = useState(false);
    const [showAllEducation, setShowAllEducation] = useState(false);
    const [isFavorite, setIsFavorite] = useState(attorney?.is_favorite || false);
    const {bannerHeight} = useContext(SearchContext);
    const [currentAttorney, setCurrentAttorney] = useState(attorney);
    const [showEditLanguagesPopup, setShowEditLanguagesPopup] = useState(false);
    const [showEditSpecializationsPopup, setShowEditSpecializationsPopup] = useState(false);
    const [showEditAwardsPopup, setShowEditAwardsPopup] = useState(false);
    const {showNotification} = useNotification();
    const [showEditProfilePopup, setShowEditProfilePopup] = useState(false);
    const [showEditEducationsPopup, setShowEditEducationsPopup] = useState(false);
    const [showEditPreviousWorkplacesPopup, setShowEditPreviousWorkplacesPopup] = useState(false);
    const {user} = useUser();
    const [showSendMessageModal, setShowSendMessageModal] = useState(false);
    const [isCircleCrop, setIsCircleCrop] = useState(false);
    const [showCropModal, setShowCropModal] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);
    const [currentFieldToUpdate, setCurrentFieldToUpdate] = useState(null);
    const [currentAspectRatio, setCurrentAspectRatio] = useState(1.0);
    const [showRequestCallbackModal, setShowRequestCallbackModal] = useState(false); // State for callback modal

    // --- Config ---
    const getRequestConfig = () => {
        const token = localStorage.getItem('authToken');
        const csrfToken = Cookies.get('csrftoken');
        return {
            headers: {'Authorization': `Bearer ${token}`, 'X-CSRFToken': csrfToken,},
            withCredentials: true,
        };
    };

    // Auto-open edit profile when instructed via origin
    useEffect(() => {
        if (visible && origin === 'edit-profile') {
            setShowEditProfilePopup(true);
        }
    }, [visible, origin]);

    // --- Effects ---
    // Fetch initial attorney details
    useEffect(() => {
        if (!attorney?.attorney_id) return;
        const fetchInitialAttorneyDetails = async (attorneyId) => {
            try {
                const response = await api.get(`/attorneys/${attorneyId}/`, {withoutAuth: true});
                setCurrentAttorney(response.data);
            } catch (error) {
                console.error('Error fetching initial attorney details:', error);
            }
        };
        fetchInitialAttorneyDetails(attorney.attorney_id);
    }, [attorney?.attorney_id]);

    // Fetch associated legal companies
    useEffect(() => {
        // Use currentAttorney state which is updated by the effect above
        if (currentAttorney?.attorney_id) {
            const fetchLegalCompanies = async () => {
                try {
                    // Assuming public endpoint
                    const response = await api.get(`/attorneys/${currentAttorney.attorney_id}/legal-companies/`, {withoutAuth: true});
                    // Ensure the response data contains the 'active_plans' array for each company
                    setLegalCompanies(response.data || []);
                } catch (error) {
                    console.error('Error fetching legal companies:', error);
                    setLegalCompanies([]); // Reset on error
                }
            };
            fetchLegalCompanies();
        } else {
            setLegalCompanies([]); // Clear if no attorney
        }
    }, [currentAttorney?.attorney_id]); // Depend on the current attorney's ID

    // Check favorite status
    useEffect(() => {
        if (!attorney?.attorney_id || !user) {
            setIsFavorite(false);
            return;
        }
        const checkFavoriteStatus = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const config = {headers: {'Authorization': `Bearer ${token}`}};
                const response = await api.get(`/check-favorite-attorney/${attorney.attorney_id}/`, config);
                setIsFavorite(response.data.is_favorite);
            } catch (error) {
                if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                    setIsFavorite(false);
                }
            }
        };
        checkFavoriteStatus();
    }, [attorney?.attorney_id, user]);

    // Body class effect
    useEffect(() => {
        if (visible) {
            document.body.classList.add('fixed-height');
        } else {
            document.body.classList.remove('fixed-height');
        }
        return () => {
            document.body.classList.remove('fixed-height');
        };
    }, [visible]);


    // --- Helper Functions ---
    const formatCompanyAddress = (address) => {
        if (!address || !address.city || !address.state || !address.country || !address.zip_code) return 'Address not available';
        const {street_address, city, state, country, zip_code} = address;
        return `${street_address || ''}, ${city?.name || ''}, ${state?.name || ''}, ${country?.name || ''}, ${zip_code?.zip_code || ''}`.replace(/ ,/g, '').trim().replace(/,$/, '');
    };
    const googleMapsUrlForCompanyAddress = (address) => {
        const formattedAddress = formatCompanyAddress(address);
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formattedAddress)}`; // Ensure correct map URL
    };
    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return '';
        let cleaned = ('' + phoneNumber).replace(/\D/g, '');
        if (cleaned.length === 10 && !cleaned.startsWith('1')) {
            cleaned = '1' + cleaned;
        }
        const match = cleaned.match(/^(\d{1})?(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            const country = match[1] ? `+${match[1]} ` : '+1 ';
            return `${country}(${match[2]}) ${match[3]}-${match[4]}`;
        }
        return phoneNumber;
    };
    const formatAmount = (amount) => {
        if (amount == null || isNaN(amount)) return '';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0
        }).format(amount);
    };
    const isCurrentUserAttorney = () => {
        if (!currentAttorney?.user || !user) {
            return false;
        }
        return user.id === currentAttorney.user;
    };
    const fetchAttorneyDetails = async (attorneyId) => {
        if (!attorneyId) {
            console.error("fetchAttorneyDetails called without ID");
            return;
        }
        try {
            const response = await api.get(`/attorneys/${attorneyId}/`, {withoutAuth: true});
            setCurrentAttorney(response.data);
            setIsFavorite(response.data.is_favorite);
            // Re-fetch associated companies as well if attorney details change? Maybe not necessary unless affiliation changes.
            // await fetchLegalCompanies(); // Uncomment if needed
        } catch (error) {
            console.error('Error re-fetching attorney details:', error);
            showNotification('Error', 'Could not refresh attorney details.', null, 'error');
        }
    };
    const calculateDuration = (startDate, endDate) => {
        if (!startDate || !endDate) return '';
        try {
            const start = new Date(startDate);
            const end = new Date(endDate);
            if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return '';
            const diffInMonths = (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth();
            if (diffInMonths < 0) return '';
            const years = Math.floor(diffInMonths / 12);
            const months = diffInMonths % 12;
            let durationStr = '';
            if (years > 0) durationStr += `${years} year${years > 1 ? 's' : ''}`;
            if (months > 0) {
                if (years > 0) durationStr += ' ';
                durationStr += `${months} month${months > 1 ? 's' : ''}`;
            }
            return durationStr ? `${durationStr} in this position` : '';
        } catch (e) {
            console.error("Error calculating duration:", e);
            return '';
        }
    };
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid Date';
            return new Intl.DateTimeFormat('en-US', {month: 'long', day: 'numeric', year: 'numeric'}).format(date);
        } catch (e) {
            console.error("Error formatting date:", e);
            return 'Invalid Date';
        }
    };
    const renderFreeConsultation = () => {
        if (currentAttorney?.free_consultation) {
            return (<span className="vendor__status vendor__status-active">FREE CONSULTATION</span>);
        }
        return null;
    };


    // --- Edit Popup Handlers ---
    const handlerShowEditLanguagesPopup = useCallback(() => {
        setShowEditLanguagesPopup(true);
    }, []);
    const handlerCloseEditLanguagesPopup = useCallback(() => {
        setShowEditLanguagesPopup(false);
    }, []);
    const handlerShowEditSpecializationsPopup = useCallback(() => {
        setShowEditSpecializationsPopup(true);
    }, []);
    const handlerCloseEditSpecializationsPopup = useCallback(() => {
        setShowEditSpecializationsPopup(false);
    }, []);
    const handlerShowEditAwardsPopup = useCallback(() => {
        setShowEditAwardsPopup(true);
    }, []);
    const handlerCloseEditAwardsPopup = useCallback(() => {
        setShowEditAwardsPopup(false);
    }, []);
    const handlerShowEditProfilePopup = useCallback(() => {
        setShowEditProfilePopup(true);
    }, []);
    const handlerCloseEditProfilePopup = useCallback(() => {
        setShowEditProfilePopup(false);
    }, []);
    const handlerShowEditEducationsPopup = useCallback(() => {
        setShowEditEducationsPopup(true);
    }, []);
    const handlerCloseEditEducationsPopup = useCallback(() => {
        setShowEditEducationsPopup(false);
    }, []);
    const handlerShowEditPreviousWorkplacesPopup = useCallback(() => {
        setShowEditPreviousWorkplacesPopup(true);
    }, []);
    const handlerCloseEditPreviousWorkplacesPopup = useCallback(() => {
        setShowEditPreviousWorkplacesPopup(false);
    }, []);

    // --- Save Handlers ---
    const handleSaveProfile = async (updatedData) => {
        if (!currentAttorney?.attorney_id) return;
        try {
            const config = getRequestConfig();
            const response = await api.patch(`/attorneys/${currentAttorney.attorney_id}/update/`, updatedData, config);
            if (response.status === 200) {
                await fetchAttorneyDetails(currentAttorney.attorney_id);
                handlerCloseEditProfilePopup();
                showNotification('Success', 'Profile updated.', null, 'success');
            }
        } catch (error) {
            console.error('Error saving profile:', error.response?.data || error);
            showNotification('Error', 'Failed to save profile.', null, 'error');
        }
    };
    const handleSaveEducations = async (updatedData) => {
        if (!currentAttorney?.attorney_id) return;
        try {
            const config = getRequestConfig();
            const response = await api.patch(`/attorneys/${currentAttorney.attorney_id}/educations/update/`, updatedData, config);
            if (response.status === 200) {
                await fetchAttorneyDetails(currentAttorney.attorney_id);
                handlerCloseEditEducationsPopup();
                showNotification('Success', 'Education updated.', null, 'success');
            }
        } catch (error) {
            console.error('Error saving educations:', error.response?.data || error);
            showNotification('Error', 'Failed to save education.', null, 'error');
        }
    };
    const handleSaveSpecializations = async (updatedSpecializations) => {
        if (!currentAttorney?.attorney_id) return;
        try {
            const specializationsToSave = updatedSpecializations.map(spec => spec.specialization_id || spec.id);
            const config = getRequestConfig();
            const response = await api.patch(`/attorneys/${currentAttorney.attorney_id}/specializations/update/`, {specializations: specializationsToSave}, config);
            if (response.status === 200) {
                await fetchAttorneyDetails(currentAttorney.attorney_id);
                handlerCloseEditSpecializationsPopup();
                showNotification('Success', 'Services updated.', null, 'success');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Failed to save services.';
            showNotification('Error', errorMsg, null, 'error');
        }
    };
    const handleSavePreviousWorkplaces = async (updatedData) => {
        if (!currentAttorney?.attorney_id) return;
        try {
            const config = getRequestConfig();
            const response = await api.patch(`/attorneys/${currentAttorney.attorney_id}/previous-workplaces/update/`, updatedData, config);
            if (response.status === 200) {
                await fetchAttorneyDetails(currentAttorney.attorney_id);
                handlerCloseEditPreviousWorkplacesPopup();
                showNotification('Success', 'Work history updated.', null, 'success');
            }
        } catch (error) {
            console.error('Error saving previous workplaces:', error.response?.data || error);
            showNotification('Error', 'Failed to save work history.', null, 'error');
        }
    };
    const handleSaveLanguages = async (updatedData) => {
        if (!currentAttorney?.attorney_id) return;
        try {
            const languagesToSave = updatedData.map(language => ({
                language_id: language.language_id || language.id,
                proficiency_level: language.proficiency_level
            }));
            const config = getRequestConfig();
            const response = await api.patch(`/attorneys/${currentAttorney.attorney_id}/languages/`, {languages: languagesToSave}, config);
            if (response.status === 200) {
                await fetchAttorneyDetails(currentAttorney.attorney_id);
                handlerCloseEditLanguagesPopup();
                showNotification('Success', 'Languages updated.', null, 'success');
            }
        } catch (error) {
            console.error('Error saving languages:', error.response?.data || error);
            showNotification('Error', 'Failed to save languages.', null, 'error');
        }
    };
    const handleSaveAwards = async (formDataToSend) => {
        if (!currentAttorney?.attorney_id) return;
        try {
            const config = getRequestConfig();
            const response = await api.patch(`/attorneys/${currentAttorney.attorney_id}/awards/update/`, formDataToSend, config);
            if (response.status === 200) {
                await fetchAttorneyDetails(currentAttorney.attorney_id);
                handlerCloseEditAwardsPopup();
                showNotification('Success', 'Awards updated successfully.', null, 'success');
            }
        } catch (error) {
            console.error('Error saving awards:', error.response?.data || error);
            showNotification('Error', 'Failed to save awards.', null, 'error');
        }
    };

    // --- Favorite Handlers ---
    const handleAddToFavorites = async () => {
        if (!user) {
            showNotification("Login Required", "Please log in to manage favorites.", null, "warning");
            return;
        }
        if (!currentAttorney?.attorney_id) return;
        try {
            const config = getRequestConfig();
            const response = await api.post('/add-favorite-attorney/', {attorney_id: currentAttorney.attorney_id}, config);
            if (response.status === 200 || response.status === 201) {
                setIsFavorite(true);
                showNotification('Success', 'Added to favorites.', null, 'success');
            }
        } catch (error) {
            console.error('Error adding to favorites:', error.response?.data || error);
            showNotification('Error', 'Could not add favorite.', null, 'error');
        }
    };
    const handleRemoveFromFavorites = async () => {
        if (!user) {
            showNotification("Login Required", "Please log in to manage favorites.", null, "warning");
            return;
        }
        if (!currentAttorney?.attorney_id) return;
        try {
            const config = getRequestConfig();
            const response = await api.post('/remove-favorite-attorney/', {attorney_id: currentAttorney.attorney_id}, config);
            if (response.status === 200 || response.status === 204) {
                setIsFavorite(false);
                showNotification('Success', 'Removed from favorites.', null, 'info');
            }
        } catch (error) {
            console.error('Error removing from favorites:', error.response?.data || error);
            showNotification('Error', 'Could not remove favorite.', null, 'error');
        }
    };
    const handleToggleFavorites = () => {
        if (isFavorite) {
            handleRemoveFromFavorites();
        } else {
            handleAddToFavorites();
        }
    };

    // --- Image Handlers ---
    const handleFieldUpdate = async (fieldName, value) => {
        if (!currentAttorney?.attorney_id) return;
        const formData = new FormData();
        formData.append(fieldName, value);
        try {
            const config = getRequestConfig();
            const response = await api.patch(`/attorneys/${currentAttorney.attorney_id}/files/`, formData, config);
            if (response.status === 200) {
                await fetchAttorneyDetails(currentAttorney.attorney_id);
                showNotification('Success', `${fieldName.replace('_', ' ')} updated.`, null, 'success');
            } else {
                showNotification('Warning', `Update failed with status ${response.status}.`, null, 'warning');
            }
        } catch (error) {
            console.error('Error updating field:', error.response?.data || error);
            showNotification('Error', `Failed to update ${fieldName.replace('_', ' ')}.`, null, 'error');
        }
    };
    const handlePhotoChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const tempURL = URL.createObjectURL(file);
            setImageToCrop(tempURL);
            setCurrentFieldToUpdate('attorney_photo');
            setCurrentAspectRatio(IMAGE_REQUIREMENTS['attorney_photo'].aspect_ratio);
            setIsCircleCrop(true);
            setShowCropModal(true);
            event.target.value = null;
        }
    };
    const handleBackgroundImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const tempURL = URL.createObjectURL(file);
            setImageToCrop(tempURL);
            setCurrentFieldToUpdate('background_image');
            setCurrentAspectRatio(IMAGE_REQUIREMENTS['background_attorney'].aspect_ratio);
            setIsCircleCrop(false);
            setShowCropModal(true);
            event.target.value = null;
        }
    };
    const handleCroppedImageComplete = async (croppedImageBlob) => {
        try {
            const file = new File([croppedImageBlob], 'cropped_image.jpg', {type: 'image/jpeg'});

            // --- FIX IS HERE ---
            // Change the condition to check for the correct field name
            const requirements = (currentFieldToUpdate === 'attorney_photo')
                ? IMAGE_REQUIREMENTS['attorney_photo']
                : IMAGE_REQUIREMENTS['background_attorney'];

            const validatedAndScaledFile = await validateAndScaleImage(
                file,
                requirements.min_width,
                requirements.min_height,
                requirements.aspect_ratio,
                requirements.ratio_tolerance
            );

            // The field name passed here was already correct
            await handleFieldUpdate(currentFieldToUpdate, validatedAndScaledFile);

        } catch (error) {
            // It's a good idea to stringify the error if it's an object
            const errorMessage = typeof error === 'object' ? error.message : error;
            showNotification('Image Validation Error', errorMessage, null, 'error');
        }

        // Reset state
        setShowCropModal(false);
        setImageToCrop(null);
        setCurrentFieldToUpdate(null);
    };

    // --- Request Callback Modal Handlers ---
    const handleShowRequestCallbackModal = useCallback(() => {
        setShowRequestCallbackModal(true);
    }, []);
    const handleCloseRequestCallbackModal = useCallback(() => {
        setShowRequestCallbackModal(false);
    }, []);

    // --- Send Message Modal Handlers ---
    const handleSendMessageClick = () => {
        if (!user) {
            showNotification("Login Required", "Please log in.", null, "warning");
            return;
        }
        setShowSendMessageModal(true);
    };
    const handleCloseSendMessageModal = () => {
        setShowSendMessageModal(false);
    };

    // --- Early Return ---
    if (!visible || !currentAttorney?.attorney_id) {
        return null;
    }

    // --- Derived State for Rendering ---
    const formattedAddress = currentAttorney?.addresses && currentAttorney.addresses.length > 0 ? currentAttorney.addresses.map(addr => `${addr.street_address || ''}, ${addr.city?.name || ''}, ${addr.state?.name || ''}, ${addr.country?.name || ''}, ${addr.zip_code?.zip_code || ''}`.replace(/ , |,$/g, '').trim()).join('\n') : 'Address not available';
    const googleMapsUrl = currentAttorney?.addresses && currentAttorney.addresses.length > 0 ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formattedAddress.split('\n')[0])}` : '#'; // Ensure correct map URL
    const companiesToShow = showAllCompanies ? (legalCompanies || []) : (legalCompanies || []).slice(0, 3);
    const previousWorkPlacesToShow = showAllPreviousWorkplaces ? (currentAttorney?.previous_working_places || []) : (currentAttorney?.previous_working_places || []).slice(0, 2);
    const educationToShow = showAllEducation ? (currentAttorney?.education || []) : (currentAttorney?.education || []).slice(0, 2);

    // *** Determine if the primary associated company has active plans ***
    // Safely access the first company and its plans
    const primaryCompanyHasActivePlans = !!(legalCompanies?.[0]?.active_plans?.length > 0);

    // --- Render ---
    return (
        <div className={`popup ${visible ? 'visible' : ''}`} id="attorney-type1"
             style={{top: `${bannerHeight + 50}px`}}>
            <Notification/>
            <button className="close js--hide-popup" onClick={onClose}><img src="/assets/icon--close-CN6QlgAA.svg"
                                                                            alt="Close"/></button>
            <div className="profile profile-pro">
                {/* Headline, Avatar, Edit Buttons... */}
                <div className="profile__headline profile__headline-attorney"
                     style={{background: `url(${currentAttorney.background_image || '/assets/default-attorney-bg.jpg'})`}}>
                    <div className="container">
                        <div className="avatar">
                            {currentAttorney.photo ? (<picture><img src={currentAttorney.photo}
                                                                    alt={`${currentAttorney.first_name} ${currentAttorney.last_name}`}/>
                            </picture>) : (<div className="avatar-placeholder"></div>)}
                            {isCurrentUserAttorney() && (<label className="userpick__edit" title="Edit Photo"> <img
                                src="/assets/icon--edit-bg-BVfPEI-f.svg" alt="edit"/> <input type="file"
                                                                                             accept="image/*"
                                                                                             onChange={handlePhotoChange}/>
                            </label>)}
                        </div>
                        {isCurrentUserAttorney() && (<label className="bg__edit" title="Edit Background Photo">
                            <div className="button no-style"><img src="/assets/icon--edit-Z9QfuMgm.svg"
                                                                  alt="edit"/> Edit Background
                            </div>
                            <input type="file" accept="image/*" onChange={handleBackgroundImageChange}/> </label>)}
                    </div>
                </div>
                {/* Profile Block */}
                <div className="profile__block">
                    <div className="container">
                        {/* Header */}
                        <div className="profile__header">
                            <div className="vendor__header">
                                <h1 className="vendor__name">{currentAttorney.first_name} {currentAttorney.last_name}</h1>
                                <button className={`favorites ${isFavorite ? 'added' : ''}`}
                                        onClick={handleToggleFavorites}
                                        aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
                                    <svg viewBox="0 0 20 20">
                                        <path fillRule="evenodd" clipRule="evenodd"
                                              d="M1.58733 3.10072C3.70393 0.940385 7.13582 0.940385 9.25246 3.10072L10.0323 3.89665L10.7475 3.16683C12.8641 1.0065 16.296 1.0065 18.4127 3.16683C20.5293 5.32717 20.5292 8.82998 18.4127 10.9904L10.4695 19.0901C10.4418 19.1184 10.5003 19.068 10.4695 19.0901C10.2068 19.2849 9.79324 19.2991 9.5566 19.0576L1.58757 10.9239C-0.52903 8.76353 -0.529268 5.26111 1.58733 3.10072Z"></path>
                                    </svg>
                                </button>
                                <div
                                    className="vendor__attorney"> {currentAttorney.license_year && `Licensed in ${currentAttorney.license_year}`} {currentAttorney.total_won_amount != null && ` • Total won: ${formatAmount(currentAttorney.total_won_amount)} `} {renderFreeConsultation()} </div>
                            </div>
                            {isCurrentUserAttorney() && (
                                <button className="button no-style" onClick={handlerShowEditProfilePopup}><img
                                    src="/assets/icon--edit-Z9QfuMgm.svg" alt="Edit"/> Edit profile </button>)}
                        </div>
                        {/* Content */}
                        <div className="profile__content userdata">
                            <div className="col-2">
                                <div className="data">
                                    <p>{currentAttorney.description || "No description available."}</p></div>
                                <div className="profile__contacts">
                                    <ul className="contacts">
                                        {/* Address */}
                                        {currentAttorney.addresses && currentAttorney.addresses.length > 0 && (
                                            <li className="contacts__el">
                                                <div className="contacts__label">Address</div>
                                                <div
                                                    className="contacts__value"> {formattedAddress.split('\n').map((line, idx) => (
                                                    <span key={idx} style={{display: 'block'}}> <a href={googleMapsUrl}
                                                                                                   target="_blank"
                                                                                                   rel="noopener noreferrer">{line}</a> </span>))} </div>
                                            </li>)}
                                        {/* Working Hours */}
                                        {currentAttorney.working_hours && currentAttorney.working_hours.length > 0 && (
                                            <li className="contacts__el">
                                                <div className="contacts__label">Working Hours</div>
                                                <div className="contacts__value">
                                                    <ul className="hours"> {currentAttorney.working_hours.map((wh, index) => (
                                                        <li key={index}>
                                                            <span>{wh.day_of_week}:</span><span>{wh.start_time} - {wh.end_time}</span>
                                                        </li>))} </ul>
                                                </div>
                                            </li>)}
                                        {/* Email */}
                                        {currentAttorney.email && (
                                            <li className="contacts__el">
                                                <div className="contacts__label">Email</div>
                                                <div className="contacts__value">
                                                    {/* Shows Send Message button if not current user */}
                                                    {isCurrentUserAttorney() ? (
                                                        <a href={`mailto:${currentAttorney.email}`}>{currentAttorney.email}</a>) : (
                                                        <button onClick={handleSendMessageClick} type='button'
                                                                className='button button-black' style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}> Send Message </button>)}
                                                </div>
                                            </li>
                                        )}
                                        {/* Phone */}
                                        {currentAttorney.phone_number && (
                                            <li className="contacts__el">
                                                <div className="contacts__label">Phone</div>
                                                <div className="contacts__value">
                                                    {/* MODIFIED LOGIC: Show link if owner OR if primary company has active plans */}
                                                    {(isCurrentUserAttorney() || primaryCompanyHasActivePlans) ? (
                                                        // If viewing own profile OR primary company has plan, show direct link
                                                        <a href={`tel:${currentAttorney.phone_number}`}>{formatPhoneNumber(currentAttorney.phone_number)}</a>
                                                    ) : (
                                                        // Otherwise (not owner AND no active plan on primary company), show Request Callback button
                                                        <button
                                                            onClick={handleShowRequestCallbackModal}
                                                            type='button'
                                                            className='button button-black button-request-callback' // Use specific class
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                        >
                                                            Request Callback
                                                        </button>
                                                    )}
                                                </div>
                                            </li>
                                        )}
                                        {/* Additional Phones */}
                                        {(currentAttorney.additional_phones || []).map((phone, index) => (
                                            <li key={index} className="contacts__el">
                                                <div
                                                    className="contacts__label">{phone.phone_type?.name || 'Phone'}</div>
                                                <div className="contacts__value"><a
                                                    href={`tel:${phone.phone_number}`}>{formatPhoneNumber(phone.phone_number)}</a>
                                                </div>
                                            </li>))}
                                        {/* Web Site */}
                                        {currentAttorney.web_site && (<li className="contacts__el">
                                            <div className="contacts__label">Web Site</div>
                                            <div className="contacts__value"><a
                                                href={currentAttorney.web_site.startsWith('http') ? currentAttorney.web_site : `https://${currentAttorney.web_site}`}
                                                target="_blank" rel="noopener noreferrer">{currentAttorney.web_site}</a>
                                            </div>
                                        </li>)}
                                        {/* Social Media */}
                                        {currentAttorney.social_media && currentAttorney.social_media.length > 0 && (
                                            <li className="contacts__el">
                                                <div className="contacts__label">Social Media</div>
                                                <div className="contacts__value">
                                                    <ul className="social"> {currentAttorney.social_media.map((sm, index) => (
                                                        <li key={index}><a
                                                            href={sm.url.startsWith('http') ? sm.url : `https://${sm.url}`}
                                                            target="_blank" rel="noopener noreferrer"
                                                            aria-label={sm.platform?.name}> {sm.platform?.icon ? (
                                                            <img src={sm.platform.icon}
                                                                 alt={sm.platform.name}/>) : (sm.platform?.name)} </a>
                                                        </li>))} </ul>
                                                </div>
                                            </li>)}
                                    </ul>
                                    {/* Share Button */}
                                    {showSharePopup && typeof showSharePopup === 'function' && (<button
                                        onClick={() => showSharePopup({
                                            companyName: `${currentAttorney.first_name} ${currentAttorney.last_name}`,
                                            telephone: currentAttorney.phone_number,
                                            address: formattedAddress,
                                            specializations: (currentAttorney.specializations || []).map(spec => spec.name)
                                        })} type='button' className='profile__share button no-style'> Share Profile <img
                                        src="/assets/icon--share.svg" alt="Share Icon"/></button>)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Other sections (Services, Languages, Workplaces, Education, Awards) */}
                {(isCurrentUserAttorney() || (currentAttorney.specializations && currentAttorney.specializations.length > 0)) && (
                    <div className="profile__block">
                        <div className="container">
                            <div className="profile__header"><h2>Types of services
                                provided</h2> {isCurrentUserAttorney() && (
                                <button className="button no-style" onClick={handlerShowEditSpecializationsPopup}><img
                                    src="/assets/icon--edit-Z9QfuMgm.svg"
                                    alt="Edit Services"/> {(!currentAttorney.specializations || currentAttorney.specializations.length === 0) ? 'Add Services' : 'Edit Services'}
                                </button>)} </div>
                            <div className="profile__content">
                                <ul className="params__list"> {(currentAttorney.specializations || []).map((spec, index) => (
                                    <li key={spec.id || spec.specialization_id || index}>{spec.name}</li>))} {(!currentAttorney.specializations || currentAttorney.specializations.length === 0) && !isCurrentUserAttorney() && (
                                    <li>No services listed.</li>)} </ul>
                            </div>
                        </div>
                    </div>)}
                {(isCurrentUserAttorney() || (currentAttorney.languages && currentAttorney.languages.length > 0)) && (
                    <div className="profile__block">
                        <div className="container">
                            <div className="profile__header"><h2>Languages</h2> {isCurrentUserAttorney() && (
                                <button className="button no-style" onClick={handlerShowEditLanguagesPopup}><img
                                    src="/assets/icon--edit-Z9QfuMgm.svg"
                                    alt="Edit Languages"/> {(!currentAttorney.languages || currentAttorney.languages.length === 0) ? 'Add Languages' : 'Edit Languages'}
                                </button>)} </div>
                            <div className="profile__content">
                                <ul className="params__list"> {(currentAttorney.languages || []).map((lang, index) => (
                                    <li key={lang.language_id || lang.id || index}>{lang.name} ({lang.proficiency_level})</li>))} {(!currentAttorney.languages || currentAttorney.languages.length === 0) && !isCurrentUserAttorney() && (
                                    <li>No languages listed.</li>)} </ul>
                            </div>
                        </div>
                    </div>)}
                {(legalCompanies || []).length > 0 && (<div className="profile__block">
                    <div className="container">
                        <div className="profile__header"><h2>Current Workplaces</h2></div>
                        <div className="profile__content">
                            <ul className="companies-list"> {companiesToShow.map((company, index) => (
                                <li className={`vendor ${company.is_verified ? 'vendor-verified' : ''}`}
                                    key={company.id || index}>
                                    <div className="container">
                                        <div className="justify">
                                            <div className="vendor__data" style={{cursor: 'default'}}>
                                                <div className="vendor__header"><h4
                                                    className="vendor__name">{company.name}</h4> {company.is_verified && (
                                                    <div className="verified"><img
                                                        src="/assets/icon--verified-CxOX3a18.svg" alt="Verified"/>
                                                    </div>)} <span
                                                    className="vendor__attorney">{(company.attorneys?.length || 0)} attorney{(company.attorneys?.length || 0) !== 1 ? 's' : ''} on staff</span>
                                                    <p className="vendor__description">{company.description}</p></div>
                                            </div>
                                            <div className="vendor__contacts"> {company.address && (
                                                <div className="row"><a className="vendor__link"
                                                                        href={googleMapsUrlForCompanyAddress(company.address)}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer">{formatCompanyAddress(company.address)}</a>
                                                    <span className="vendor__label">Address</span>
                                                </div>)}
                                                {company.phone_number && (
                                                    <div className="row">
                                                        {/*<a className="vendor__link"*/}
                                                        {/*   href={`tel:${company.phone_number}`}>{formatPhoneNumber(company.phone_number)}</a>*/}
                                                        {/*<span className="vendor__label">Phone</span>*/}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </li>))} {!showAllCompanies && (legalCompanies || []).length > 3 && (
                                <li className="show-all">
                                    <button className="button-link" onClick={() => setShowAllCompanies(true)}> Show More
                                        • {(legalCompanies || []).length - 3} (Total: {(legalCompanies || []).length})
                                    </button>
                                </li>)} {showAllCompanies && (legalCompanies || []).length > 3 && (
                                <li className="show-all">
                                    <button className="button-link" onClick={() => setShowAllCompanies(false)}> Show
                                        Less
                                    </button>
                                </li>)} </ul>
                        </div>
                    </div>
                </div>)}
                {(isCurrentUserAttorney() || (currentAttorney.previous_working_places && currentAttorney.previous_working_places.length > 0)) && (
                    <div className="profile__block">
                        <div className="container">
                            <div className="profile__header"><h2>Previous Workplaces</h2> {isCurrentUserAttorney() && (
                                <button className="button no-style" onClick={handlerShowEditPreviousWorkplacesPopup}>
                                    <img src="/assets/icon--edit-Z9QfuMgm.svg"
                                         alt="Edit Workplaces"/> {(!currentAttorney.previous_working_places || currentAttorney.previous_working_places.length === 0) ? 'Add Workplace' : 'Edit Workplaces'}
                                </button>)} </div>
                            <div className="profile__content">
                                <div
                                    className="col-2 decor-list"> {(previousWorkPlacesToShow || []).map((place, index) => (
                                    <div className="vendor decor-list__el" key={place.id || index}>
                                        <div className="vendor__header"><h4 className="vendor__name">{place.name}</h4>
                                            <span className="vendor__attorney">{place.position}</span> <span
                                                className="vendor__attorney">{formatDate(place.start_date)} - {formatDate(place.end_date)}</span>
                                            <span
                                                className="vendor__attorney">{calculateDuration(place.start_date, place.end_date)}</span>
                                        </div>
                                    </div>))} {(!currentAttorney.previous_working_places || currentAttorney.previous_working_places.length === 0) && !isCurrentUserAttorney() && (
                                    <li>No previous workplaces listed.</li>)} </div>
                                {!showAllPreviousWorkplaces && (currentAttorney.previous_working_places || []).length > 2 && (
                                    <div className="show-all">
                                        <button className="button-link"
                                                onClick={() => setShowAllPreviousWorkplaces(true)}>Show More
                                            • {(currentAttorney.previous_working_places || []).length - 2} (Total: {(currentAttorney.previous_working_places || []).length})
                                        </button>
                                    </div>)} {showAllPreviousWorkplaces && (currentAttorney.previous_working_places || []).length > 2 && (
                                <div className="show-all">
                                    <button className="button-link"
                                            onClick={() => setShowAllPreviousWorkplaces(false)}>Show Less
                                    </button>
                                </div>)} </div>
                        </div>
                    </div>)}
                {(isCurrentUserAttorney() || (currentAttorney.education && currentAttorney.education.length > 0)) && (
                    <div className="profile__block">
                        <div className="container">
                            <div className="profile__header"><h2>Education</h2> {isCurrentUserAttorney() && (
                                <button className="button no-style" onClick={handlerShowEditEducationsPopup}><img
                                    src="/assets/icon--edit-Z9QfuMgm.svg"
                                    alt="Edit Education"/> {(!currentAttorney.education || currentAttorney.education.length === 0) ? 'Add Education' : 'Edit Education'}
                                </button>)} </div>
                            <div className="profile__content">
                                <div className="col-2 decor-list"> {(educationToShow || []).map((edu, index) => (
                                    <div className="vendor decor-list__el" key={edu.id || index}>
                                        <div className="vendor__header"><h4
                                            className="vendor__name">{edu.institution}</h4> <span
                                            className="vendor__attorney">{edu.degree}</span> <span
                                            className="vendor__attorney">{formatDate(edu.start_date)} - {formatDate(edu.end_date)}</span>
                                        </div>
                                    </div>))} {(!currentAttorney.education || currentAttorney.education.length === 0) && !isCurrentUserAttorney() && (
                                    <li>No education listed.</li>)} </div>
                                {!showAllEducation && (currentAttorney.education || []).length > 2 && (
                                    <div className="show-all">
                                        <button className="button-link" onClick={() => setShowAllEducation(true)}>Show
                                            More
                                            • {(currentAttorney.education || []).length - 2} (Total: {(currentAttorney.education || []).length})
                                        </button>
                                    </div>)} {showAllEducation && (currentAttorney.education || []).length > 2 && (
                                <div className="show-all">
                                    <button className="button-link" onClick={() => setShowAllEducation(false)}>Show
                                        Less
                                    </button>
                                </div>)} </div>
                        </div>
                    </div>)}
                {(isCurrentUserAttorney() || (currentAttorney.awards && currentAttorney.awards.length > 0)) && (
                    <div className="profile__block">
                        <div className="container">
                            <div className="profile__header"><h2>Awards and Articles</h2> {isCurrentUserAttorney() && (
                                <button className="button no-style" onClick={handlerShowEditAwardsPopup}><img
                                    src="/assets/icon--edit-Z9QfuMgm.svg"
                                    alt="Edit Awards"/> {(!currentAttorney.awards || currentAttorney.awards.length === 0) ? 'Add Awards' : 'Edit Awards'}
                                </button>)} </div>
                            <div className="profile__content">
                                <div
                                    className="col-2 decor-list"> {(currentAttorney.awards || []).map((award, index) => (
                                    <div key={award.id || index} className="vendor vendor-award decor-list__el">
                                        <div className="vendor__award"> {award.image &&
                                            <img src={award.image} alt={award.title || 'Award'}/>} </div>
                                        <div className="vendor__header"><h4 className="vendor__name">{award.title}</h4>
                                            <span className="vendor__attorney">{award.subtitle}</span></div>
                                    </div>))} {(!currentAttorney.awards || currentAttorney.awards.length === 0) && !isCurrentUserAttorney() && (
                                    <li>No awards listed.</li>)} </div>
                            </div>
                        </div>
                    </div>)}
            </div>
            {/* End .profile */}

            {/* --- Modals --- */}
            {showEditLanguagesPopup && (<EditLanguagesPopup show={showEditLanguagesPopup}
                                                            closeEditLanguagesPopup={handlerCloseEditLanguagesPopup}
                                                            initialData={currentAttorney}
                                                            onSave={handleSaveLanguages}/>)}
            {showEditPreviousWorkplacesPopup && (<EditPreviousWorkplacesPopup show={showEditPreviousWorkplacesPopup}
                                                                              onClose={handlerCloseEditPreviousWorkplacesPopup}
                                                                              initialData={currentAttorney}
                                                                              onSave={handleSavePreviousWorkplaces}/>)}
            {showEditAwardsPopup && (<EditAwardsPopup show={showEditAwardsPopup} onClose={handlerCloseEditAwardsPopup}
                                                      initialData={currentAttorney} onSave={handleSaveAwards}/>)}
            {showEditProfilePopup && (
                <EditProfilePopup show={showEditProfilePopup} closeEditProfilePopup={handlerCloseEditProfilePopup}
                                  initialData={currentAttorney} onSave={handleSaveProfile} isAttorney={true}/>)}
            {showEditEducationsPopup && (
                <EditEducationsPopup show={showEditEducationsPopup} onClose={handlerCloseEditEducationsPopup}
                                     initialData={currentAttorney} onSave={handleSaveEducations}/>)}
            {showEditSpecializationsPopup && (<EditSpecializationsPopup show={showEditSpecializationsPopup}
                                                                        onClose={handlerCloseEditSpecializationsPopup}
                                                                        initialData={currentAttorney}
                                                                        onSave={handleSaveSpecializations}/>)}
            {/* Send Message Modal */}
            {showSendMessageModal && (
                <SendMessageModal show={showSendMessageModal} recipient={currentAttorney} recipientType="attorney"
                                  onClose={handleCloseSendMessageModal}/>)}
            {/* Request Callback Modal */}
            {showRequestCallbackModal && (
                <RequestCallbackModal show={showRequestCallbackModal} recipient={currentAttorney}
                                      recipientType="attorney" onClose={handleCloseRequestCallbackModal}/>)}
            {/* Crop Modal */}
            {showCropModal && imageToCrop && (<CropImageModal show={showCropModal} onClose={() => {
                if (imageToCrop) URL.revokeObjectURL(imageToCrop);
                setShowCropModal(false);
                setImageToCrop(null);
                setCurrentFieldToUpdate(null);
            }} image={imageToCrop} aspect={currentAspectRatio} onComplete={handleCroppedImageComplete}
                                                              isCircle={isCircleCrop}/>)}
        </div> // End .popup
    );
};

export default PopupAttorney;
